export type ThemeName = 'retro80s' | 'cyberpunk' | 'neonNight' | 'gameboy' | 'matrix';

export interface Theme {
  name: ThemeName;
  label: string;
  colors: {
    primary: string;
    secondary: string;
    background: string;
    text: string;
    neonPink: string;
    neonCyan: string;
    neonGreen: string;
    neonYellow: string;
    neonPurple: string;
    border: string;
    buttonBg: string;
    gridLines: string;
  };
  font: string;
  hasGlow: boolean;
  hasScanlines: boolean;
}

export const themes: Record<ThemeName, Theme> = {
  retro80s: {
    name: 'retro80s',
    label: 'Retro 80s',
    colors: {
      primary: '#ff00ff',
      secondary: '#00ffff',
      background: '#0a0a0a',
      text: '#ffff00',
      neonPink: '#ff00ff',
      neonCyan: '#00ffff',
      neonGreen: '#00ff00',
      neonYellow: '#ffff00',
      neonPurple: '#bf00ff',
      border: '#00ffff',
      buttonBg: '#1a1a2e',
      gridLines: '#ff00ff'
    },
    font: 'Press Start 2P, monospace',
    hasGlow: true,
    hasScanlines: true
  },
  cyberpunk: {
    name: 'cyberpunk',
    label: 'Cyberpunk',
    colors: {
      primary: '#ff6b00',
      secondary: '#00f0ff',
      background: '#0d0d0d',
      text: '#e0e0e0',
      neonPink: '#ff0055',
      neonCyan: '#00f0ff',
      neonGreen: '#39ff14',
      neonYellow: '#fcee0a',
      neonPurple: '#bc13fe',
      border: '#00f0ff',
      buttonBg: '#1a1a2e',
      gridLines: '#ff6b00'
    },
    font: 'Orbitron, sans-serif',
    hasGlow: true,
    hasScanlines: false
  },
  neonNight: {
    name: 'neonNight',
    label: 'Neon Night',
    colors: {
      primary: '#ff0080',
      secondary: '#00d4ff',
      background: '#0a0a1a',
      text: '#ffffff',
      neonPink: '#ff0080',
      neonCyan: '#00d4ff',
      neonGreen: '#00ff88',
      neonYellow: '#ffd700',
      neonPurple: '#b347d9',
      border: '#00d4ff',
      buttonBg: '#12122a',
      gridLines: '#ff0080'
    },
    font: 'Russo One, sans-serif',
    hasGlow: true,
    hasScanlines: true
  },
  gameboy: {
    name: 'gameboy',
    label: 'Game Boy',
    colors: {
      primary: '#0f380f',
      secondary: '#306230',
      background: '#9bbc0f',
      text: '#0f380f',
      neonPink: '#8bac0f',
      neonCyan: '#306230',
      neonGreen: '#0f380f',
      neonYellow: '#8bac0f',
      neonPurple: '#0f380f',
      border: '#306230',
      buttonBg: '#8bac0f',
      gridLines: '#306230'
    },
    font: 'Courier New, monospace',
    hasGlow: false,
    hasScanlines: false
  },
  matrix: {
    name: 'matrix',
    label: 'Matrix',
    colors: {
      primary: '#00ff00',
      secondary: '#003300',
      background: '#000000',
      text: '#00ff00',
      neonPink: '#00cc00',
      neonCyan: '#00ff00',
      neonGreen: '#00ff00',
      neonYellow: '#ccffcc',
      neonPurple: '#00aa00',
      border: '#00ff00',
      buttonBg: '#001100',
      gridLines: '#00ff00'
    },
    font: 'Courier New, monospace',
    hasGlow: true,
    hasScanlines: true
  }
};

export const defaultTheme: ThemeName = 'retro80s';

export class ThemeService {
  private static STORAGE_KEY = 'gato_retro_theme';

  static getCurrentTheme(): ThemeName {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored && stored in themes) {
        return stored as ThemeName;
      }
    } catch {
      return defaultTheme;
    }
    return defaultTheme;
  }

  static setTheme(themeName: ThemeName): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, themeName);
      this.applyTheme(themeName);
    } catch {
      console.error('Error saving theme');
    }
  }

  static applyTheme(themeName: ThemeName): void {
    const theme = themes[themeName] || themes[defaultTheme];
    const root = document.documentElement;

    const hexToRgb = (hex: string): string => {
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `${r}, ${g}, ${b}`;
    };

    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-secondary', theme.colors.secondary);
    root.style.setProperty('--color-bg', theme.colors.background);
    root.style.setProperty('--color-text', theme.colors.text);
    root.style.setProperty('--color-neon-pink', theme.colors.neonPink);
    root.style.setProperty('--color-neon-cyan', theme.colors.neonCyan);
    root.style.setProperty('--color-neon-green', theme.colors.neonGreen);
    root.style.setProperty('--color-neon-yellow', theme.colors.neonYellow);
    root.style.setProperty('--color-neon-purple', theme.colors.neonPurple);
    root.style.setProperty('--color-border', theme.colors.border);
    root.style.setProperty('--color-button-bg', theme.colors.buttonBg);
    root.style.setProperty('--color-grid-lines', theme.colors.gridLines);
    root.style.setProperty('--font-retro', theme.font);

    // RGB triplets for Tailwind alpha modifiers (e.g. bg-retro-dark/80)
    root.style.setProperty('--color-bg-rgb', hexToRgb(theme.colors.background));
    root.style.setProperty('--color-button-bg-rgb', hexToRgb(theme.colors.buttonBg));
    root.style.setProperty('--color-neon-pink-rgb', hexToRgb(theme.colors.neonPink));
    root.style.setProperty('--color-neon-cyan-rgb', hexToRgb(theme.colors.neonCyan));
    root.style.setProperty('--color-neon-green-rgb', hexToRgb(theme.colors.neonGreen));
    root.style.setProperty('--color-neon-yellow-rgb', hexToRgb(theme.colors.neonYellow));
    root.style.setProperty('--color-neon-purple-rgb', hexToRgb(theme.colors.neonPurple));
    root.style.setProperty('--color-primary-rgb', hexToRgb(theme.colors.primary));

    document.body.classList.toggle('glow-effect', theme.hasGlow);
    document.body.classList.toggle('scanlines', theme.hasScanlines);
  }

  static getThemeCSS(): string {
    const theme = themes[this.getCurrentTheme()];
    return `
      --color-primary: ${theme.colors.primary};
      --color-secondary: ${theme.colors.secondary};
      --color-bg: ${theme.colors.background};
      --color-text: ${theme.colors.text};
      --color-neon-pink: ${theme.colors.neonPink};
      --color-neon-cyan: ${theme.colors.neonCyan};
      --color-neon-green: ${theme.colors.neonGreen};
      --color-neon-yellow: ${theme.colors.neonYellow};
      --color-neon-purple: ${theme.colors.neonPurple};
      --color-border: ${theme.colors.border};
      --color-button-bg: ${theme.colors.buttonBg};
      --color-grid-lines: ${theme.colors.gridLines};
      --font-retro: ${theme.font};
    `;
  }
}

export default ThemeService;
